import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import DynamicTable from '../../components/DynamicTable';
import Pagination from '../../components/Pagination';
import TradeInListingDrawer from '../../components/TradeInListingDrawer';
import SpuInfoDrawer from '../../components/SpuInfoDrawer';
import BrandInfoDrawer from '../../components/BrandInfoDrawer';
import FilterDrawer from "../../components/FilterDrawer";
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import noImage from '../../assets/img/brand/noImage.png';
import { getDeviceDefaultImage } from './tradeInImages.js';
import apiHelper from '../../apiHelper';
import ConfirmDeactivateModal from '../../components/ConfirmDeactivateModal';
import ConfirmActivateModal from '../../components/ConfirmActivateModal';
import ConfirmArchiveModal from '../../components/ConfirmArchiveModal';
import AlertNotification from '../../components/AlertNotification';
import { useLoader } from '../../components/GlobalLoader';
import { formatDateTime } from '../../utils/formateDate';

const PAGE_SIZE = 20;

// ─── Component ────────────────────────────────────────────────────────────────
export default function TradeInListing() {
    const history  = useHistory();
    const location = useLocation();

    // ── Data & Tabs ──
    const [pageData, setPageData]         = useState([]);
    const [totalCount, setTotalCount]     = useState(0);
    const [archPageData, setArchPageData] = useState([]);
    const [archTotalCount, setArchTotalCount] = useState(0);
    // Set when a list request fails; the table then shows "Couldn't load" with Try again
    const [loadError, setLoadError]           = useState(false);
    const [archLoadError, setArchLoadError]   = useState(false);
    const [loading, setLoading]           = useState(true);
    const [activeTab, setActiveTab]       = useState('listing');
    const { showLoader, hideLoader } = useLoader();

    // ── Filter options (fetched once) ──
    const [filterOptions, setFilterOptions]         = useState({ listing_status: [], listing_device: [], listing_brand: [] });
    const [archFilterOptions, setArchFilterOptions] = useState({ listing_device: [], listing_brand: [] });

    // ── Search ──
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm,  setSearchTerm]  = useState('');

    // ── Sort ──
    const [sortField, setSortField] = useState('listing_priority');
    const [sortOrder, setSortOrder] = useState('desc');

    // ── Pagination — initialise from returnPage if coming back from Items page ──
    const [currentPage, setCurrentPage] = useState(() => location.state?.returnPage || 1);

    // ── Filter (active tab) ──
    const [appliedFilter,   setAppliedFilter]   = useState(null);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [tempValues, setTempValues] = useState({ listing_status: [], listing_device: [], listing_brand: [] });

    // ── Action dropdown (active tab) ──
    const [dropdownOpenId, setDropdownOpenId]     = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs  = useRef({});

    // ── Archived tab state ──
    const [archSearchInput, setArchSearchInput]   = useState('');
    const [archSearchTerm,  setArchSearchTerm]    = useState('');
    const [archSortField,   setArchSortField]     = useState(null);
    const [archSortOrder,   setArchSortOrder]     = useState('asc');
    const [archCurrentPage, setArchCurrentPage]   = useState(1);
    const [archDrawerRow,   setArchDrawerRow]     = useState(null);
    const [archAppliedFilter, setArchAppliedFilter]   = useState(null);
    const [archFilterPopupOpen, setArchFilterPopupOpen] = useState(false);
    const [archTempValues, setArchTempValues]     = useState({ listing_device: [], listing_brand: [] });
    const [archDropdownOpenId, setArchDropdownOpenId]     = useState(null);
    const [archDropdownPosition, setArchDropdownPosition] = useState({ top: 0, right: 0 });
    const archDropdownRef = useRef(null);
    const archButtonRefs  = useRef({});

    // ── Prefetch cache (active listings) ──
    const prefetchCache      = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    // ── Prefetch cache (archived listings) ──
    const archPrefetchCache      = useRef(new Map());
    const archPrefetchInProgress = useRef(new Set());
    // ── Numbers each list request, so a slower older response can't replace a newer one ──
    const loadSeq     = useRef(0);
    const archLoadSeq = useRef(0);
    // ── Skip-first-render guards ──
    const isFirstActiveRender = useRef(true);
    const isFirstArchRender   = useRef(true);

    // ── Fetch helpers ──
    const buildActiveParams = (page, search, filter, sortFld, sortOrd) => {
        const p = new URLSearchParams({ archived: 'false', page, page_size: PAGE_SIZE });
        if (search) p.set('search', search);
        if (sortFld) { p.set('sort_by', sortFld); p.set('sort_order', sortOrd); }
        if (filter?.listing_status?.length) filter.listing_status.forEach(v => p.append('listing_status[]', v));
        if (filter?.listing_device?.length)  filter.listing_device.forEach(v => p.append('listing_device[]', v));
        if (filter?.listing_brand?.length)   filter.listing_brand.forEach(v => p.append('listing_brand[]', v));
        return p.toString();
    };

    const buildArchParams = (page, search, filter, sortFld, sortOrd) => {
        const p = new URLSearchParams({ archived: 'true', page, page_size: PAGE_SIZE });
        if (search) p.set('search', search);
        if (sortFld) { p.set('sort_by', sortFld); p.set('sort_order', sortOrd); }
        if (filter?.listing_device?.length) filter.listing_device.forEach(v => p.append('listing_device[]', v));
        if (filter?.listing_brand?.length)  filter.listing_brand.forEach(v => p.append('listing_brand[]', v));
        return p.toString();
    };

    const buildActiveCacheKey = (page, search, filter, sortFld, sortOrd) =>
        JSON.stringify({ page, search: (search || '').trim(), filter, sortFld, sortOrd });

    const buildArchCacheKey = (page, search, filter, sortFld, sortOrd) =>
        JSON.stringify({ page, search: (search || '').trim(), filter, sortFld, sortOrd });

    const prefetchActivePage = async (page, search, filter, sortFld, sortOrd, totalCnt) => {
        const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildActiveCacheKey(page, search, filter, sortFld, sortOrd);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const res = await apiHelper(`sales/trade-in-listings/?${buildActiveParams(page, search, filter, sortFld, sortOrd)}`);
            if (res?.ok) {
                const data = await res.json();
                prefetchCache.current.set(key, { results: data.results ?? [], count: data.count ?? 0 });
            }
        } catch (_) { /* silent */ } finally { prefetchInProgress.current.delete(key); }
    };

    const prefetchArchPage = async (page, search, filter, sortFld, sortOrd, totalCnt) => {
        const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildArchCacheKey(page, search, filter, sortFld, sortOrd);
        if (archPrefetchCache.current.has(key) || archPrefetchInProgress.current.has(key)) return;
        archPrefetchInProgress.current.add(key);
        try {
            const res = await apiHelper(`sales/trade-in-listings/?${buildArchParams(page, search, filter, sortFld, sortOrd)}`);
            if (res?.ok) {
                const data = await res.json();
                archPrefetchCache.current.set(key, { results: data.results ?? [], count: data.count ?? 0 });
            }
        } catch (_) { /* silent */ } finally { archPrefetchInProgress.current.delete(key); }
    };

    const fetchPage = async (page, search, filter, sortFld, sortOrd, isInitial = false) => {
        // Taken before the cache check, so a cached page also cancels older requests
        const seq = ++loadSeq.current;
        const key = buildActiveCacheKey(page, search, filter, sortFld, sortOrd);
        const cached = prefetchCache.current.get(key);
        if (cached) {
            setLoadError(false);
            setPageData(cached.results);
            setTotalCount(cached.count);
            prefetchActivePage(page + 1, search, filter, sortFld, sortOrd, cached.count);
            return;
        }
        if (isInitial) { setLoading(true); showLoader(); }
        try {
            const res = await apiHelper(`sales/trade-in-listings/?${buildActiveParams(page, search, filter, sortFld, sortOrd)}`);
            if (res?.ok) {
                const data = await res.json();
                // A newer page/search/filter/sort request has started; drop this response
                if (seq !== loadSeq.current) return;
                const results = data.results ?? [];
                const count   = data.count   ?? 0;
                prefetchCache.current.set(key, { results, count });
                setLoadError(false);
                setPageData(results);
                setTotalCount(count);
                prefetchActivePage(page + 1, search, filter, sortFld, sortOrd, count);
            } else if (seq === loadSeq.current) {
                // Show the failure; don't leave the previous search's rows on screen
                setLoadError(true); setPageData([]); setTotalCount(0);
            }
        } catch (err) {
            console.error('Failed to fetch trade-in listings:', err);
            if (seq === loadSeq.current) { setLoadError(true); setPageData([]); setTotalCount(0); }
        } finally {
            if (isInitial) { setLoading(false); hideLoader(); }
        }
    };

    const fetchArchPage = async (page, search, filter, sortFld, sortOrd) => {
        const seq = ++archLoadSeq.current;
        const key = buildArchCacheKey(page, search, filter, sortFld, sortOrd);
        const cached = archPrefetchCache.current.get(key);
        if (cached) {
            setArchLoadError(false);
            setArchPageData(cached.results);
            setArchTotalCount(cached.count);
            prefetchArchPage(page + 1, search, filter, sortFld, sortOrd, cached.count);
            return;
        }
        try {
            const res = await apiHelper(`sales/trade-in-listings/?${buildArchParams(page, search, filter, sortFld, sortOrd)}`);
            if (res?.ok) {
                const data = await res.json();
                if (seq !== archLoadSeq.current) return;
                const results = data.results ?? [];
                const count   = data.count   ?? 0;
                archPrefetchCache.current.set(key, { results, count });
                setArchLoadError(false);
                setArchPageData(results);
                setArchTotalCount(count);
                prefetchArchPage(page + 1, search, filter, sortFld, sortOrd, count);
            } else if (seq === archLoadSeq.current) {
                setArchLoadError(true); setArchPageData([]); setArchTotalCount(0);
            }
        } catch (err) {
            console.error('Failed to fetch archived trade-in listings:', err);
            if (seq === archLoadSeq.current) { setArchLoadError(true); setArchPageData([]); setArchTotalCount(0); }
        }
    };

    // ── Fetch active listings whenever page/search/filter/sort changes ──
    useEffect(() => {
        const isInitial = currentPage === (location.state?.returnPage || 1) && !searchTerm && !appliedFilter;
        fetchPage(currentPage, searchTerm, appliedFilter, sortField, sortOrder, isInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, appliedFilter, sortField, sortOrder]);

    // ── Fetch archived listings when archived tab is active ──
    useEffect(() => {
        if (activeTab !== 'archived') return;
        fetchArchPage(archCurrentPage, archSearchTerm, archAppliedFilter, archSortField, archSortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, archCurrentPage, archSearchTerm, archAppliedFilter, archSortField, archSortOrder]);

    // ── Clear active cache when search/filter/sort changes ──
    useEffect(() => {
        if (isFirstActiveRender.current) { isFirstActiveRender.current = false; return; }
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, appliedFilter, sortField, sortOrder]);

    // ── Clear archived cache when search/filter/sort changes ──
    useEffect(() => {
        if (isFirstArchRender.current) { isFirstArchRender.current = false; return; }
        archPrefetchCache.current.clear();
        archPrefetchInProgress.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archSearchTerm, archAppliedFilter, archSortField, archSortOrder]);

    // ── Fetch filter options once on mount ──
    useEffect(() => {
        async function fetchFilterOptions() {
            try {
                const [activeOpts, archOpts] = await Promise.all([
                    apiHelper('sales/trade-in-listings/filter-options/?archived=false'),
                    apiHelper('sales/trade-in-listings/filter-options/?archived=true'),
                ]);
                if (activeOpts?.ok) setFilterOptions(await activeOpts.json());
                if (archOpts?.ok)   setArchFilterOptions(await archOpts.json());
            } catch (err) {
                console.error('Failed to fetch filter options:', err);
            }
        }
        fetchFilterOptions();
    }, []);

    // ── Listing Info Drawer ──
    const [drawerRow, setDrawerRow] = useState(null);
    const [pendingDrawerId, setPendingDrawerId] = useState(null);

    const openDrawer  = (row) => setDrawerRow(row);
    const closeDrawer = ()    => setDrawerRow(null);

    // ── SPU Info Drawer (nested inside Trade-In drawer) ──
    const [spuInfoDrawerOpen, setSpuInfoDrawerOpen] = useState(false);
    const [selectedSpuId,     setSelectedSpuId]     = useState(null);

    const handleSpuClick = (row) => {
        if (!row.spu_id) return;
        setSelectedSpuId(row.spu_id);
        setSpuInfoDrawerOpen(true);
    };

    // ── Brand Info Drawer (nested inside SPU drawer) ──
    const [brandInfoDrawerOpen, setBrandInfoDrawerOpen] = useState(false);
    const [selectedBrandRow,    setSelectedBrandRow]    = useState(null);

    const handleBrandClick = async (brandId) => {
        if (!brandId) return;
        try {
            const res = await apiHelper(`inventory/brands/show/${brandId}/`, 'GET');
            if (res && res.ok) {
                const data = await res.json();
                setSelectedBrandRow(data);
                setBrandInfoDrawerOpen(true);
            }
        } catch (err) {
            console.error('Failed to load brand:', err);
        }
    };

    // ── Modal state ──
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [activateModalOpen,   setActivateModalOpen]   = useState(false);
    const [archiveModalOpen,    setArchiveModalOpen]    = useState(false);
    const [pendingActionRow,    setPendingActionRow]    = useState(null);
    const [notification, setNotification] = useState({ show: false, type: '', recordName: '', action: '' });

    // ── Open modals ──
    const handleToggleStatus = (row) => {
        setPendingActionRow(row);
        if (row.listing_status !== 'Active') {
            setActivateModalOpen(true);
        } else {
            setDeactivateModalOpen(true);
        }
    };

    const handleArchive = (row) => {
        setPendingActionRow(row);
        setArchiveModalOpen(true);
    };

    // ── Confirm handlers ──
    const handleConfirmActivate = async () => {
        setActivateModalOpen(false);
        await executeToggleStatus(pendingActionRow);
        setPendingActionRow(null);
    };

    const handleConfirmDeactivate = async () => {
        setDeactivateModalOpen(false);
        await executeToggleStatus(pendingActionRow);
        setPendingActionRow(null);
    };

    const handleConfirmArchive = async () => {
        setArchiveModalOpen(false);
        await executeArchive(pendingActionRow);
        setPendingActionRow(null);
    };

    // ── Actual API calls ──
    const executeToggleStatus = async (row) => {
        const action = row.listing_status === 'Active' ? 'deactivate' : 'activate';
        try {
            const res = await apiHelper(
                `sales/trade-in-listings/${row.tradein_listing_id}/status/`,
                'PATCH',
                { action }
            );
            if (res && res.ok) {
                const updated = await res.json();
                setPageData(prev =>
                    prev.map(r => r.tradein_listing_id === updated.tradein_listing_id ? updated : r)
                );
                if (drawerRow?.tradein_listing_id === updated.tradein_listing_id) {
                    setDrawerRow(updated);
                }
                console.log('[WCM sync]', action, updated.wcm_sync);
                prefetchCache.current.clear();
                prefetchInProgress.current.clear();
                setNotification({ show: true, type: 'success', recordName: 'Trade-In Listing', action });
            } else {
                setNotification({ show: true, type: 'error', recordName: 'Trade-In Listing', action });
            }
        } catch (err) {
            console.error('Failed to update listing status:', err);
            setNotification({ show: true, type: 'error', recordName: 'Trade-In Listing', action });
        }
    };

    const executeArchive = async (row) => {
        try {
            const res = await apiHelper(
                `sales/trade-in-listings/${row.tradein_listing_id}/archive/`,
                'PATCH'
            );
            if (res && res.ok) {
                const archived = await res.json();
                console.log('[WCM sync] archive', archived.wcm_sync);
                if (drawerRow?.tradein_listing_id === row.tradein_listing_id) {
                    setDrawerRow(null);
                }
                // Refetch active page (item was removed) and bump archived count
                prefetchCache.current.clear();
                prefetchInProgress.current.clear();
                archPrefetchCache.current.clear();
                archPrefetchInProgress.current.clear();
                await fetchPage(currentPage, searchTerm, appliedFilter, sortField, sortOrder);
                setArchTotalCount(prev => prev + 1);
                setNotification({ show: true, type: 'success', recordName: 'Trade-In Listing', action: 'archive' });
            } else {
                setNotification({ show: true, type: 'error', recordName: 'Trade-In Listing', action: 'archive' });
            }
        } catch (err) {
            console.error('Failed to archive listing:', err);
            setNotification({ show: true, type: 'error', recordName: 'Trade-In Listing', action: 'archive' });
        }
    };

    // ── Restore drawer state and notification when returning from Items page ──
    useEffect(() => {
        const state = location.state;
        if (state?.returnDrawerId) {
            // currentPage already initialised from returnPage; store ID and open once data arrives
            setPendingDrawerId(state.returnDrawerId);
        }
        if (state?.successMessage) {
            setNotification({ show: true, type: 'success', message: state.successMessage });
        }
        // Clear the state so it doesn't re-apply on re-renders
        window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Open the drawer once page data has loaded (for breadcrumb return) ──
    useEffect(() => {
        if (!pendingDrawerId || pageData.length === 0) return;
        const row = pageData.find(r => r.tradein_listing_id === pendingDrawerId);
        if (row) {
            setDrawerRow(row);
            setPendingDrawerId(null);
        }
    }, [pendingDrawerId, pageData]);

    const statusOptions = filterOptions.listing_status;
    const deviceOptions = ['Phone', 'Tablet', 'Watch', 'Accessories'];
    const brandOptions  = filterOptions.listing_brand;

    const filterSections = [
        { label: 'Status',         type: 'checkbox', key: 'listing_status',  options: ["Active", "Inactive"], },
        { label: 'Listing Device', type: 'simpleList', key: 'listing_device', data: deviceOptions.map(d => ({ id: d, name: d })), renderItem: (item) => ({ id: item.id, title: item.name }) },
        { label: 'Listing Brand',  type: 'list', key: 'listing_brand',  data: brandOptions.map(b => ({ id: b, name: b })),  renderItem: (item) => ({ id: item.id, title: item.name }), showDropdownIcon: true },
    ];

    const archDeviceOptions = ['Phone', 'Tablet', 'Watch', 'Accessories'];
    const archBrandOptions  = archFilterOptions.listing_brand;

    const archFilterSections = [
        { label: 'Listing Device', type: 'list', key: 'listing_device', data: archDeviceOptions.map(d => ({ id: d, name: d })), renderItem: (item) => ({ id: item.id, title: item.name }), showDropdownIcon: true },
        { label: 'Listing Brand',  type: 'list', key: 'listing_brand',  data: archBrandOptions.map(b => ({ id: b, name: b })),  renderItem: (item) => ({ id: item.id, title: item.name }), showDropdownIcon: true },
    ];

    const breadcrumbs = ['Listing', 'Trade-In Listing'];

    // ── Sort handlers (reset to page 1 so server fetches the right page) ──
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const handleArchSort = (field) => {
        if (archSortField === field) {
            setArchSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setArchSortField(field);
            setArchSortOrder('asc');
        }
        setArchCurrentPage(1);
    };

    // ── Derived pagination values (server provides the totals) ──
    const totalPages     = Math.ceil(totalCount / PAGE_SIZE) || 1;
    const archTotalPages = Math.ceil(archTotalCount / PAGE_SIZE) || 1;

    // ── Dropdown open/close ──
    const toggleDropdown = (id, e) => {
        if (e) e.stopPropagation();
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
            return;
        }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setDropdownPosition({
                top:   rect.bottom + window.scrollY + 4,
                right: window.innerWidth - rect.right,
            });
        }
        setDropdownOpenId(id);
    };

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpenId(null);
            }
        }
        if (dropdownOpenId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    // Close dropdown on scroll
    useEffect(() => {
        const handleScroll = () => setDropdownOpenId(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    const currentRow = (pageData ?? []).find(r => r.tradein_listing_id === dropdownOpenId);

    // ── Active styling on 3-dot button (Listings tab) ──
    useEffect(() => {
        Object.entries(buttonRefs.current).forEach(([id, btn]) => {
            if (!btn) return;
            const isOpen = dropdownOpenId !== null && Number(id) === dropdownOpenId;
            btn.style.borderRadius = isOpen ? '8px' : '';
            btn.style.background   = isOpen ? '#FFF' : 'transparent';
            btn.style.boxShadow    = isOpen ? '0 1px 4px 0 rgba(0,0,0,0.10)' : 'none';
            btn.style.transition   = 'background 0.15s ease, box-shadow 0.15s ease';
        });
    }, [dropdownOpenId]);

    // ── Archived dropdown open/close ──
    const toggleArchDropdown = (id, e) => {
        if (e) e.stopPropagation();
        if (archDropdownOpenId === id) {
            setArchDropdownOpenId(null);
            return;
        }
        const btn = archButtonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setArchDropdownPosition({
                top:   rect.bottom + window.scrollY + 4,
                right: window.innerWidth - rect.right,
            });
        }
        setArchDropdownOpenId(id);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (archDropdownRef.current && !archDropdownRef.current.contains(event.target)) {
                setArchDropdownOpenId(null);
            }
        }
        if (archDropdownOpenId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [archDropdownOpenId]);

    useEffect(() => {
        const handleScroll = () => setArchDropdownOpenId(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    const archCurrentRow = (archPageData ?? []).find(r => r.tradein_listing_id === archDropdownOpenId);

    // ── Active styling on 3-dot button (Archived tab) ──
    useEffect(() => {
        Object.entries(archButtonRefs.current).forEach(([id, btn]) => {
            if (!btn) return;
            const isOpen = archDropdownOpenId !== null && Number(id) === archDropdownOpenId;
            btn.style.borderRadius = isOpen ? '8px' : '';
            btn.style.background   = isOpen ? '#FFF' : 'transparent';
            btn.style.boxShadow    = isOpen ? '0 1px 4px 0 rgba(0,0,0,0.10)' : 'none';
            btn.style.transition   = 'background 0.15s ease, box-shadow 0.15s ease';
        });
    }, [archDropdownOpenId]);

    // ── DynamicTable columns ──
    const columns = [
        {
            label: 'Priority',
            field: 'listing_priority',
            sortable: true,
            width: '100px',
        },
        {
            label: 'Listing Model',
            field: 'listing_model',
            sortable: true,
            width: '200px',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Mirror the listing-level image: a listing sitting on its device default
                        has tradein_image_url = null, and a generic no-image icon here reads as
                        a broken listing rather than a defaulted one (ERP-5022). */}
                    <img
                        src={row.tradein_image_url || getDeviceDefaultImage(row.listing_device)}
                        alt={row.listing_model}
                        onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                        style={{
                            width: 32,
                            height: 32,
                            objectFit: 'cover',
                            borderRadius: 4,
                            flexShrink: 0,
                        }}
                    />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.listing_model}
                    </span>
                </div>
            ),
        },
        {
            label: 'Status',
            field: 'listing_status',
            sortable: true,
            width: '110px',
            // The .status chip carries min-width: 110px in company.scss and the cell
            // adds 10px padding either side, so 130px is the point where the pill
            // starts getting clipped. Chips can't fall back to an ellipsis.
            minWidth: 130,
        },
        {
            label: 'ID',
            field: 'listing_code',
            sortable: true,
            width: '90px',
        },
        {
            label: 'Listing Device',
            field: 'listing_device',
            sortable: true,
            width: '140px',
        },
        {
            label: 'Listing Brand',
            field: 'listing_brand',
            sortable: true,
            width: '130px',
        },
        {
            label: 'SPU',
            field: 'spu',
            sortable: true,
            width: '180px',
        },
        {
            label: 'Last Modified Time',
            field: 'modified_at',
            sortable: true,
            width: '170px',
            render: (row) => formatDateTime(row.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>,
        },
    ];

    const isInitialTableEmpty = totalCount === 0 && !loading && !searchTerm && !appliedFilter;

    // ── Archived table columns ──
    const archColumns = [
        {
            label: 'Listing Model',
            field: 'listing_model',
            sortable: true,
            width: '200px',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                        src={row.tradein_image_url || getDeviceDefaultImage(row.listing_device)}
                        alt={row.listing_model}
                        onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                        style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                    />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.listing_model}
                    </span>
                </div>
            ),
        },
        {
            label: 'ID',
            field: 'listing_code',
            sortable: true,
            width: '90px',
            render: (row) => (
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {row.listing_code || <span style={{ color: '#ADB5BD' }}>NA</span>}
                </span>
            ),
        },
        {
            label: 'Listing Device',
            field: 'listing_device',
            sortable: true,
            width: '140px',
        },
        {
            label: 'Listing Brand',
            field: 'listing_brand',
            sortable: true,
            width: '130px',
        },
        {
            label: 'SPU',
            field: 'spu',
            sortable: true,
            width: '180px',
        },
        {
            label: 'Archived Time',
            field: 'archived_at',
            sortable: true,
            width: '170px',
            render: (row) => formatDateTime(row.archived_at) || <span style={{ color: '#ADB5BD' }}>NA</span>,
        },
    ];

    const isArchTableEmpty = archTotalCount === 0 && !archSearchTerm && !archAppliedFilter;

    // ── Filter icons (exact match from PlatformOrderPage) ───────────────────
    const FilterActiveIcon = () => (
        <svg
            style={{ margin: 0 }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333"
                stroke="#0D6EFD"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <g clipPath="url(#clip0_4516_4830)">
                <path
                    d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z"
                    fill="#0D6EFD"
                />
            </g>
            <defs>
                <clipPath id="clip0_4516_4830">
                    <rect width="12" height="12" fill="white" transform="translate(11 5)" />
                </clipPath>
            </defs>
        </svg>
    );

    const FilterInactiveIcon = ({ color = "#6C757D" }) => (
        <svg
            style={{ margin: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
    const emptyTitle   = searchTerm ? 'No Results Found' : 'No Listings Found';
    const emptyMessage = searchTerm
        ? 'No results match what you\'re looking for'
        : 'Nothing here yet. Add your first item to get started!';
    const emptyImage   = searchTerm ? emptySearchImg : noBrandImage;

    const archEmptyTitle   = archSearchTerm ? 'No Results Found' : 'No Archived Listings Found';
    const archEmptyMessage = archSearchTerm
        ? 'No results match what you\'re looking for'
        : 'No archived listings yet.';
    const archEmptyImage   = archSearchTerm ? emptySearchImg : noBrandImage;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            {/* Breadcrumbs */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1 && (
                            <span className="breadcrumb-separator">  </span>
                        )}
                    </span>
                ))}
            </div>

            <div className="employee-tabs-container">
                {/* ─── Tab Buttons ─── */}
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'listing' ? 'active' : ''}`}
                        onClick={() => setActiveTab('listing')}
                    >
                        Listings
                    </button>
                    <button
                        className={`tab ${activeTab === 'archived' ? 'active' : ''}`}
                        onClick={() => setActiveTab('archived')}
                    >
                        Archived Listings
                    </button>
                </div>

                {/* ─── Listings Tab ─── */}
                {activeTab === 'listing' && (
                    <div className="tab-content" id="trade-in-listing-content">
                        <div className="info-section">
                            <div className="info-card" style={{ overflow: 'unset' }}>
                                <div className="entities-tab">
                                    {/* Toolbar */}
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <div style={{ display: "flex", gap: "8px" }}>

                                            {/* Search Box */}
                                            <div className="search-box" style={{ width: "300px", position: 'relative', border: isInitialTableEmpty ? "1px solid var(--Gray-400, #CED4DA)" : "1px solid var(--Gray-600, #6C757D)", background: isInitialTableEmpty ? "var(--Gray-100, #F8F9FA)" : "var(--Default-White, #FFF)", pointerEvents: isInitialTableEmpty ? "none" : "auto" }}>
                                                <Search size={20} color={isInitialTableEmpty ? "#CED4DA" : "#6C757D"} />
                                                {isInitialTableEmpty && (
                                                    <style>{`.search-box-disabled-til::placeholder { color: #CED4DA !important; }`}</style>
                                                )}
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    disabled={isInitialTableEmpty}
                                                    className={isInitialTableEmpty ? 'search-box-disabled-til' : undefined}
                                                    style={{ color: isInitialTableEmpty ? '#ADB5BD' : undefined }}
                                                    value={searchInput}
                                                    onChange={(e) => setSearchInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            setSearchTerm(searchInput);
                                                            setCurrentPage(1);
                                                        }
                                                    }}
                                                />
                                                {searchInput && (
                                                    <button
                                                        onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                                                        style={{
                                                            position: 'absolute', right: '4px',
                                                            background: 'transparent', border: 'none',
                                                            cursor: 'pointer', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center', padding: '4px',
                                                        }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Filter Button */}
                                            <button
                                                className={`icon-btn p-2 filter-btn ${appliedFilter ? "applied" : ""}`}
                                                onClick={() => {
                                                    if (isInitialTableEmpty) return;
                                                    if (!filterPopupOpen && appliedFilter) {
                                                        setTempValues({ ...appliedFilter });
                                                    } else if (!filterPopupOpen) {
                                                        setTempValues({ listing_status: [], listing_device: [], listing_brand: [] });
                                                    }
                                                    setFilterPopupOpen(prev => !prev);
                                                }}
                                                disabled={isInitialTableEmpty}
                                                style={{
                                                    border: isInitialTableEmpty
                                                        ? "1px solid var(--Gray-400, #CED4DA)"
                                                        : appliedFilter
                                                            ? "1px solid #2C7BE5"
                                                            : "1px solid var(--Gray-600, #6C757D)",
                                                    color: isInitialTableEmpty ? "#CED4DA" : appliedFilter ? "#2C7BE5" : "#6C757D",
                                                    background: isInitialTableEmpty
                                                        ? "var(--Gray-100, #F8F9FA)"
                                                        : appliedFilter
                                                            ? "#CFE2FF"
                                                            : "var(--Default-White, #FFF)",
                                                    height: "40px",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    pointerEvents: isInitialTableEmpty ? "none" : "auto",
                                                    cursor: isInitialTableEmpty ? "not-allowed" : "pointer",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                {appliedFilter && !isInitialTableEmpty ? (
                                                    <FilterActiveIcon />
                                                ) : (
                                                    <FilterInactiveIcon color={isInitialTableEmpty ? "#CED4DA" : "#6C757D"} />
                                                )}
                                            </button>

                                            <FilterDrawer
                                                isOpen={filterPopupOpen}
                                                onClose={() => {
                                                    setTempValues({ listing_status: [], listing_device: [], listing_brand: [] });
                                                    setFilterPopupOpen(false);
                                                }}
                                                sections={filterSections}
                                                tempValues={tempValues}
                                                setTempValues={setTempValues}
                                                onApply={() => {
                                                    const hasAny = tempValues.listing_status?.length || tempValues.listing_device?.length || tempValues.listing_brand?.length;
                                                    if (!hasAny) {
                                                        setAppliedFilter(null);
                                                    } else {
                                                        setAppliedFilter({ ...tempValues });
                                                    }
                                                    setFilterPopupOpen(false);
                                                    setCurrentPage(1);
                                                }}
                                                onClear={() => {
                                                    const empty = { listing_status: [], listing_device: [], listing_brand: [] };
                                                    setTempValues(empty);
                                                    setAppliedFilter(null);
                                                    setFilterPopupOpen(false);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        </div>

                                        {/* Add Listing button */}
                                        <div>
                                            <button
                                                className="icon-btn dark-border"
                                                onClick={() => history.push('/trade-in-listing/add-listing')}
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                <Plus size={18} /> Add Listing
                                            </button>
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="table-wrapper" style={{ border: 'none' }}>
                                        <DynamicTable
                                            columns={columns}
                                            data={pageData}
                                            loadError={loadError}
                                            onRetry={() => fetchPage(currentPage, searchTerm, appliedFilter, sortField, sortOrder)}
                                            errorWhat="trade-in listings"
                                            sortField={sortField}
                                            sortOrder={sortOrder}
                                            handleSort={handleSort}
                                            onRowClick={(row) => openDrawer(row)}
                                            onActionClick={(id, e) => toggleDropdown(id, e)}
                                            buttonRefs={buttonRefs}
                                            rowKey="tradein_listing_id"
                                            dropdownOpenId={dropdownOpenId}
                                            searchTerm={searchTerm}
                                            emptyImage={emptyImage}
                                            emptyTitle={emptyTitle}
                                            emptyMessage={emptyMessage}
                                            showActions={true}
                                            showCheckbox={false}
                                            suppressHighlight={true}
                                            stickyColumns={2}
                                            resizableColumns={true}
                                        />

                                        {/* Action Dropdown */}
                                        {dropdownOpenId !== null && currentRow && (
                                            <div
                                                ref={dropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: 'fixed',
                                                    top:   dropdownPosition.top,
                                                    right: dropdownPosition.right,
                                                    background: 'white',
                                                    boxShadow: '0px 1px 4px 0px #0000001A',
                                                    zIndex: 1000,
                                                    minWidth: 160,
                                                    borderRadius: 8,
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button onClick={() => {
                                                            openDrawer(currentRow);
                                                            setDropdownOpenId(null);
                                                        }}>
                                                            View Details
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button onClick={() => {
                                                            setDropdownOpenId(null);
                                                            history.push('/trade-in-listing/items', { listing: currentRow, returnPage: currentPage });
                                                        }}>
                                                            View all Items
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button onClick={() => {
                                                            setDropdownOpenId(null);
                                                            history.push('/trade-in-listing/edit-listing', { listing: currentRow });
                                                        }}>
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button onClick={() => {
                                                            setDropdownOpenId(null);
                                                            handleToggleStatus(currentRow);
                                                        }}>
                                                            {currentRow.listing_status === 'Active' ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button
                                                            className="deactivate-cls"
                                                            onClick={() => {
                                                                setDropdownOpenId(null);
                                                                handleArchive(currentRow);
                                                            }}
                                                        >
                                                            Archive
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', padding: '8px 0' }}>
                                         {totalCount > 0 ? (<span style={{ fontSize: '12px', fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                            {(currentPage - 1) * PAGE_SIZE + 1} - {(currentPage - 1) * PAGE_SIZE + pageData.length} of {totalCount}
                                        </span>) : ""}
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ─── Archived Listings Tab ─── */}
                {activeTab === 'archived' && (
                    <div className="tab-content" id="archived-listing-content">
                        <div className="info-section">
                            <div className="info-card" style={{ overflow: 'unset' }}>
                                <div className="entities-tab">
                                    {/* Toolbar */}
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <div style={{ display: "flex", gap: "8px" }}>

                                            {/* Search Box */}
                                            <div className="search-box" style={{ width: "300px", position: 'relative', border: isArchTableEmpty ? "1px solid var(--Gray-400, #CED4DA)" : "1px solid var(--Gray-600, #6C757D)", background: isArchTableEmpty ? "var(--Gray-100, #F8F9FA)" : "var(--Default-White, #FFF)", pointerEvents: isArchTableEmpty ? "none" : "auto" }}>
                                                <Search size={20} color={isArchTableEmpty ? "#CED4DA" : "#6C757D"} />
                                                {isArchTableEmpty && (
                                                    <style>{`.search-box-disabled-til-arch::placeholder { color: #CED4DA !important; }`}</style>
                                                )}
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    disabled={isArchTableEmpty}
                                                    className={isArchTableEmpty ? 'search-box-disabled-til-arch' : undefined}
                                                    style={{ color: isArchTableEmpty ? '#ADB5BD' : undefined }}
                                                    value={archSearchInput}
                                                    onChange={(e) => setArchSearchInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            setArchSearchTerm(archSearchInput);
                                                            setArchCurrentPage(1);
                                                        }
                                                    }}
                                                />
                                                {archSearchInput && (
                                                    <button
                                                        onClick={() => { setArchSearchInput(''); setArchSearchTerm(''); setArchCurrentPage(1); }}
                                                        style={{
                                                            position: 'absolute', right: '4px',
                                                            background: 'transparent', border: 'none',
                                                            cursor: 'pointer', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center', padding: '4px',
                                                        }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Filter Button */}
                                            <button
                                                className={`icon-btn p-2 filter-btn ${archAppliedFilter ? "applied" : ""}`}
                                                onClick={() => {
                                                    if (isArchTableEmpty) return;
                                                    if (!archFilterPopupOpen && archAppliedFilter) {
                                                        setArchTempValues({ ...archAppliedFilter });
                                                    } else if (!archFilterPopupOpen) {
                                                        setArchTempValues({ listing_device: [], listing_brand: [] });
                                                    }
                                                    setArchFilterPopupOpen(prev => !prev);
                                                }}
                                                disabled={isArchTableEmpty}
                                                style={{
                                                    border: isArchTableEmpty
                                                        ? "1px solid var(--Gray-400, #CED4DA)"
                                                        : archAppliedFilter
                                                            ? "1px solid #2C7BE5"
                                                            : "1px solid var(--Gray-600, #6C757D)",
                                                    color: isArchTableEmpty ? "#CED4DA" : archAppliedFilter ? "#2C7BE5" : "#6C757D",
                                                    background: isArchTableEmpty
                                                        ? "var(--Gray-100, #F8F9FA)"
                                                        : archAppliedFilter
                                                            ? "#CFE2FF"
                                                            : "var(--Default-White, #FFF)",
                                                    height: "40px",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    pointerEvents: isArchTableEmpty ? "none" : "auto",
                                                    cursor: isArchTableEmpty ? "not-allowed" : "pointer",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                {archAppliedFilter && !isArchTableEmpty ? (
                                                    <FilterActiveIcon />
                                                ) : (
                                                    <FilterInactiveIcon color={isArchTableEmpty ? "#CED4DA" : "#6C757D"} />
                                                )}
                                            </button>

                                            <FilterDrawer
                                                isOpen={archFilterPopupOpen}
                                                onClose={() => {
                                                    setArchTempValues({ listing_device: [], listing_brand: [] });
                                                    setArchFilterPopupOpen(false);
                                                }}
                                                sections={archFilterSections}
                                                tempValues={archTempValues}
                                                setTempValues={setArchTempValues}
                                                onApply={() => {
                                                    const hasAny = archTempValues.listing_device?.length || archTempValues.listing_brand?.length;
                                                    if (!hasAny) {
                                                        setArchAppliedFilter(null);
                                                    } else {
                                                        setArchAppliedFilter({ ...archTempValues });
                                                    }
                                                    setArchFilterPopupOpen(false);
                                                    setArchCurrentPage(1);
                                                }}
                                                onClear={() => {
                                                    const empty = { listing_device: [], listing_brand: [] };
                                                    setArchTempValues(empty);
                                                    setArchAppliedFilter(null);
                                                    setArchFilterPopupOpen(false);
                                                    setArchCurrentPage(1);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="table-wrapper" style={{ border: 'none' }}>
                                        <DynamicTable
                                            columns={archColumns}
                                            data={archPageData}
                                            loadError={archLoadError}
                                            onRetry={() => fetchArchPage(archCurrentPage, archSearchTerm, archAppliedFilter, archSortField, archSortOrder)}
                                            errorWhat="archived trade-in listings"
                                            sortField={archSortField}
                                            sortOrder={archSortOrder}
                                            handleSort={handleArchSort}
                                            onRowClick={(row) => setArchDrawerRow(row)}
                                            onActionClick={(id, e) => toggleArchDropdown(id, e)}
                                            buttonRefs={archButtonRefs}
                                            rowKey="tradein_listing_id"
                                            dropdownOpenId={archDropdownOpenId}
                                            searchTerm={archSearchTerm}
                                            emptyImage={archEmptyImage}
                                            emptyTitle={archEmptyTitle}
                                            emptyMessage={archEmptyMessage}
                                            showActions={true}
                                            showCheckbox={false}
                                            suppressHighlight={true}
                                            stickyColumns={2}
                                            resizableColumns={true}
                                        />

                                        {/* Archived Action Dropdown */}
                                        {archDropdownOpenId !== null && archCurrentRow && (
                                            <div
                                                ref={archDropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: 'fixed',
                                                    top:   archDropdownPosition.top,
                                                    right: archDropdownPosition.right,
                                                    background: 'white',
                                                    boxShadow: '0px 1px 4px 0px #0000001A',
                                                    zIndex: 1000,
                                                    minWidth: 160,
                                                    borderRadius: 8,
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button onClick={() => {
                                                            setArchDrawerRow(archCurrentRow);
                                                            setArchDropdownOpenId(null);
                                                        }}>
                                                            View Details
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', padding: '8px 0' }}>
                                        {archTotalCount > 0 ? (<span style={{ fontSize: '12px', fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                            {(archCurrentPage - 1) * PAGE_SIZE + 1} - {(archCurrentPage - 1) * PAGE_SIZE + archPageData.length} of {archTotalCount}
                                        </span>) : ''}
                                        <Pagination
                                            currentPage={archCurrentPage}
                                            totalPages={archTotalPages}
                                            onPageChange={setArchCurrentPage}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Listing Info Drawer ── */}
            {!spuInfoDrawerOpen && drawerRow && (
                <TradeInListingDrawer
                    row={drawerRow}
                    onClose={closeDrawer}
                    currentPage={currentPage}
                    onToggleStatus={handleToggleStatus}
                    onArchive={handleArchive}
                    onSpuClick={handleSpuClick}
                />
            )}

            {/* ── SPU Info Drawer (opened from Trade-In Listing drawer) ── */}
            {spuInfoDrawerOpen && !brandInfoDrawerOpen && (
                <SpuInfoDrawer
                    spuId={selectedSpuId}
                    onBack={() => setSpuInfoDrawerOpen(false)}
                    onClose={() => { setSpuInfoDrawerOpen(false); setDrawerRow(null); }}
                    onBrandClick={handleBrandClick}
                />
            )}

            {/* ── Brand Info Drawer (opened from SPU drawer) ── */}
            {brandInfoDrawerOpen && selectedBrandRow && (
                <BrandInfoDrawer
                    selectedRow={selectedBrandRow}
                    setSelectedRow={setSelectedBrandRow}
                    setBrandDrawerOpen={(val) => setBrandInfoDrawerOpen(val)}
                    setSpuDrawerOpen={() => { setSpuInfoDrawerOpen(false); setDrawerRow(null); }}
                    brandsData={selectedBrandRow ? [selectedBrandRow] : []}
                    setBrandsData={() => {}}
                    readOnly={true}
                />
            )}

            {/* ── Archived Listing Info Drawer ── */}
            {archDrawerRow && (
                <TradeInListingDrawer
                    row={archDrawerRow}
                    onClose={() => setArchDrawerRow(null)}
                    isArchived={true}
                />
            )}

            {/* ── Activate Confirmation Modal ── */}
            <ConfirmActivateModal
                isOpen={activateModalOpen}
                onCancel={() => { setActivateModalOpen(false); setPendingActionRow(null); }}
                onConfirm={handleConfirmActivate}
                entityName="Trade-In Listing"
            />

            {/* ── Deactivate Confirmation Modal ── */}
            <ConfirmDeactivateModal
                isOpen={deactivateModalOpen}
                onCancel={() => { setDeactivateModalOpen(false); setPendingActionRow(null); }}
                onConfirm={handleConfirmDeactivate}
                entityName="Trade-In Listing"
            />

            {/* ── Archive Confirmation Modal ── */}
            <ConfirmArchiveModal
                isOpen={archiveModalOpen}
                onCancel={() => { setArchiveModalOpen(false); setPendingActionRow(null); }}
                onConfirm={handleConfirmArchive}
                entityName="Trade-In Listing"
            />

            {/* ── Alert Notification ── */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    recordName={notification.recordName || ''}
                    action={notification.action || 'create'}
                    message={notification.message}
                    onClose={() => setNotification({ show: false, type: '', recordName: '', action: '', message: '' })}
                />
            )}

            {/* CSS — matching PlatformOrderPage */}
            <style jsx>{`
                .search-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    border: 1px solid #DEE2E6;
                    border-radius: 8px;
                    background: white;
                }
                .search-box input {
                    border: none;
                    outline: none;
                    width: 100%;
                    font-size: 14px;
                    background: transparent;
                }
            `}</style>

            {/* Replaced the old hand-crafted, hardcoded-pixel sticky-column hack
                (nth-child(2) forced sticky at a fixed left offset per tab) with
                DynamicTable's own stickyColumns={2}/resizableColumns={true} —
                same standard pattern used across Stock, Brand, Category,
                SPU/SKU, and Branch. The hardcoded offsets couldn't adapt to a
                resized column's actual width; DynamicTable measures it live. */}
            <style>{`
              /* Matches Stock List / Brand's Action column width exactly. */
              .entities-table .actionHeader {
                width: 56px;
                min-width: 56px;
                max-width: 56px;
              }

              /* Fixed-column shadows — anchored to the sticky column itself so
                 they stay correctly placed at any scroll offset, and spill onto
                 the scrollable content the fixed column is floating above. Only
                 shown while something is actually scrolled behind it. */
              .entities-table.is-scrolled-right th.actionHeader::before,
              .entities-table.is-scrolled-right td.actionColumn::before {
                content: "";
                position: absolute;
                left: -8px;
                top: 0;
                width: 8px;
                height: 100%;
                pointer-events: none;
                z-index: 1;
                background: linear-gradient(
                  to left,
                  rgba(0, 0, 0, 0.12),
                  rgba(0, 0, 0, 0)
                );
              }

              .entities-table.is-scrolled-left th:nth-child(2)::after,
              .entities-table.is-scrolled-left td:nth-child(2)::after {
                content: "";
                position: absolute;
                top: 0;
                right: -8px;
                width: 8px;
                height: 100%;
                pointer-events: none;
                z-index: 1;
                background: linear-gradient(
                  to right,
                  rgba(0, 0, 0, 0.12),
                  rgba(0, 0, 0, 0)
                );
              }

              /* Listing Model / ID's cells clip via their own inner span (see
                 columns/archColumns render above), so the outer td can safely
                 allow overflow here without losing text truncation — needed so
                 the shadow above isn't cut off by the td's own overflow:hidden. */
              .entities-table.is-scrolled-left td:nth-child(2),
              /* The th needs the same exemption or the Listing Model header
                 shows no shadow while its body cells do. This table's sticky
                 column is the 2nd child because of the leading checkbox
                 column, so the shared :first-child rule doesn't reach it. */
              .entities-table.is-scrolled-left th:nth-child(2) {
                overflow: visible !important;
              }

              /* Grip mark on each column's resize handle, so users can spot
                 where to drag without having to hover first. */
              .col-resize-handle::after {
                content: "";
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                height: 50%;
                left: 3px;
                width: 2px;
                background: #DEE2E6;
              }
            `}</style>

        </div>
    );
}
